import React, { useMemo } from "react";
import { Card, CardBody, CardHeader, Col, Row, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import CountUp from "react-countup";

import useChartColors from "Components/Common/useChartColors";
import { ActivityReportData } from "./types";

const CHART_ID = "activity-report-trend";

/** Bucket keys come back as YYYY-MM-DD, YYYY-MM or YYYY. */
const formatBucket = (bucket: string, granularity: string) => {
  if (granularity === "year") return bucket;

  const date =
    granularity === "month"
      ? new Date(`${bucket}-01T00:00:00Z`)
      : new Date(`${bucket}T00:00:00Z`);

  if (isNaN(date.getTime())) return bucket;

  return granularity === "month"
    ? date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
};

/**
 * Visits and distinct users over the selected window. The bucket size follows
 * the length of that window, so the same chart serves a week and five years.
 */
const UsageTrend = ({
  data,
  loading,
}: {
  data: ActivityReportData | null;
  loading: boolean;
}) => {
  const chartColors = useChartColors(CHART_ID);

  const points = useMemo(() => data?.trend?.points || [], [data]);
  const granularity = data?.trend?.granularity || "day";

  const categories = useMemo(
    () => points.map((point) => formatBucket(point.date, granularity)),
    [points, granularity]
  );

  const series = useMemo(
    () => [
      { name: "Page Visits", data: points.map((point) => point.visits) },
      { name: "Active Users", data: points.map((point) => point.users) },
    ],
    [points]
  );

  const summary = data?.summary?.current;

  const options: any = {
    chart: {
      height: 340,
      type: "area",
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    // Falls back to Apex's own palette until the themed colours resolve.
    colors: chartColors?.length ? chartColors : undefined,
    dataLabels: { enabled: false },
    stroke: { width: [2, 2], curve: "smooth" },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0.03 },
    },
    markers: { size: 0, hover: { sizeOffset: 4 } },
    legend: { position: "top", horizontalAlign: "right" },
    xaxis: { categories, tickAmount: Math.min(categories.length, 12) },
    yaxis: { labels: { formatter: (value: number) => Math.round(value) } },
    grid: { borderColor: "#f1f1f1" },
  };

  return (
    <Col xxl={8}>
      <Card className="card-height-100">
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Active User Stats</h4>
          <span className="text-muted fs-12 text-capitalize">
            by {granularity}
          </span>
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp start={0} end={summary?.activeUsers || 0} duration={2} />
                </h5>
                <p className="text-muted mb-0">Active Users</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp start={0} end={summary?.visits || 0} duration={2} />
                </h5>
                <p className="text-muted mb-0">Page Visits</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp
                    start={0}
                    end={summary?.pagesVisited || 0}
                    duration={2}
                  />
                </h5>
                <p className="text-muted mb-0">Pages Visited</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0 border-end-0">
                <h5 className="mb-1">
                  <CountUp
                    start={0}
                    end={summary?.activeAgencies || 0}
                    duration={2}
                  />
                </h5>
                <p className="text-muted mb-0">Agencies</p>
              </div>
            </Col>
          </Row>
        </CardHeader>

        <CardBody className="p-0 pb-2">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="sm" />
            </div>
          ) : points.length === 0 ? (
            <div className="text-center text-muted py-5">
              No activity matches the current filters.
            </div>
          ) : (
            <div className="w-100">
              <ReactApexChart
                dir="ltr"
                options={options}
                series={series}
                id={CHART_ID}
                type="area"
                height="340"
                data-colors='["--vz-primary", "--vz-success"]'
                className="apex-charts"
              />
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default UsageTrend;
